/**
 * Global Appearance Utility — FONT FAMILY + TEXT SIZE.
 *
 * Companion to theme.utils.ts (which owns the colour themes). These two knobs
 * are orthogonal: font/scale compose with any data-theme.
 *   • font  → data-font attribute on <html> (values declared in theme/appearance.css)
 *   • scale → inline root font-size (px), so the WHOLE app's rem sizing scales.
 * Both persist in localStorage.
 */

export type AppFont =
  | 'inter' | 'geist' | 'poppins' | 'rubik' | 'product-sans' | 'fikry' | 'segoe'
  | 'system' | 'rounded' | 'verdana' | 'tahoma' | 'trebuchet' | 'arial' | 'consolas';

export interface FontOption { value: AppFont; label: string; stack: string; }

/** `stack` is duplicated here (also in appearance.css) so the picker can preview
 *  each option in its own typeface. Keep the two in sync. */
export const APP_FONTS: FontOption[] = [
  { value: 'inter',        label: 'Inter',        stack: "'Inter', ui-sans-serif, system-ui, sans-serif" },
  { value: 'geist',        label: 'Geist',        stack: "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif" },
  // Free + web-served (loaded in index.css) — no install needed.
  { value: 'poppins',      label: 'Poppins',      stack: "'Poppins', 'Inter', ui-sans-serif, system-ui, sans-serif" },
  { value: 'rubik',        label: 'Rubik',        stack: "'Rubik', 'Inter', ui-sans-serif, system-ui, sans-serif" },
  // Product Sans (Google-proprietary) shows where installed; everywhere else it
  // falls back to Poppins — the web-served, licence-clean lookalike — so the app
  // still reads as Product Sans without shipping the proprietary file. Fikry has
  // no free CDN and falls back to Inter until an @font-face source is provided.
  { value: 'product-sans', label: 'Product Sans', stack: "'Product Sans', 'Google Sans', 'Poppins', 'Inter', ui-sans-serif, sans-serif" },
  { value: 'fikry',        label: 'Fikry',        stack: "'Fikry', 'Fikrey', 'Inter', ui-sans-serif, sans-serif" },
  { value: 'segoe',     label: 'Segoe UI',   stack: "'Segoe UI', system-ui, sans-serif" },
  { value: 'system',    label: 'System',     stack: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" },
  { value: 'rounded',   label: 'Rounded',    stack: "'Nunito', 'Quicksand', 'Segoe UI', system-ui, sans-serif" },
  { value: 'verdana',   label: 'Verdana',    stack: "Verdana, Geneva, sans-serif" },
  { value: 'tahoma',    label: 'Tahoma',     stack: "Tahoma, Geneva, Verdana, sans-serif" },
  { value: 'trebuchet', label: 'Trebuchet',  stack: "'Trebuchet MS', 'Segoe UI', sans-serif" },
  { value: 'arial',     label: 'Arial',      stack: "Arial, Helvetica, sans-serif" },
  { value: 'consolas',  label: 'Consolas',   stack: "Consolas, 'SF Mono', 'Cascadia Code', ui-monospace, monospace" },
];
// Serif faces (Georgia / Times / Palatino / Garamond) were removed on purpose:
// a serif turns a data product into a newspaper. Any saved serif preference now
// fails the getFont() whitelist and falls back to Inter automatically.

export const SCALE_MIN = 80;
export const SCALE_MAX = 140;
export const SCALE_STEP = 5;
export const SCALE_DEFAULT = 100;

const FONT_KEY = 'appFont';
const SCALE_KEY = 'appScale';
const DENSITY_KEY = 'appDensity';
const WEIGHT_KEY = 'appWeight';
const clampScale = (n: number) => Math.min(SCALE_MAX, Math.max(SCALE_MIN, Math.round(n / SCALE_STEP) * SCALE_STEP));

/** Row/card spacing. `compact` is the current dense default; `comfortable` adds
 *  air (roomier tables and cards) for a cleaner, less newspaper-y feel. */
export type AppDensity = 'compact' | 'comfortable';
/** Base text weight. `medium` lifts body text a touch for a more modern read. */
export type AppWeight = 'regular' | 'medium';

export const getDensity = (): AppDensity => {
  if (typeof window === 'undefined') return 'compact';
  return localStorage.getItem(DENSITY_KEY) === 'comfortable' ? 'comfortable' : 'compact';
};
export const getWeight = (): AppWeight => {
  if (typeof window === 'undefined') return 'regular';
  return localStorage.getItem(WEIGHT_KEY) === 'medium' ? 'medium' : 'regular';
};

export const getFont = (): AppFont => {
  if (typeof window === 'undefined') return 'product-sans';
  const v = localStorage.getItem(FONT_KEY) as AppFont | null;
  return v && APP_FONTS.some((f) => f.value === v) ? v : 'product-sans';
};

export const getScale = (): number => {
  if (typeof window === 'undefined') return SCALE_DEFAULT;
  const v = Number(localStorage.getItem(SCALE_KEY));
  return Number.isFinite(v) && v > 0 ? clampScale(v) : SCALE_DEFAULT;
};

/** Apply every knob to <html>: font + density + weight via data-attrs (CSS reads
 *  them), size via inline rem base. Density/weight come from storage so a single
 *  entry point stays in sync. */
export const applyAppearance = (font: AppFont, scale: number): void => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-font', font);
  root.setAttribute('data-density', getDensity());
  root.setAttribute('data-weight', getWeight());
  root.style.fontSize = `${(16 * clampScale(scale)) / 100}px`;
};

export const setFont = (font: AppFont): AppFont => {
  if (typeof window !== 'undefined') localStorage.setItem(FONT_KEY, font);
  applyAppearance(font, getScale());
  return font;
};

export const setScale = (scale: number): number => {
  const next = clampScale(scale);
  if (typeof window !== 'undefined') localStorage.setItem(SCALE_KEY, String(next));
  applyAppearance(getFont(), next);
  return next;
};

export const setDensity = (density: AppDensity): AppDensity => {
  if (typeof window !== 'undefined') localStorage.setItem(DENSITY_KEY, density);
  applyAppearance(getFont(), getScale());
  return density;
};

export const setWeight = (weight: AppWeight): AppWeight => {
  if (typeof window !== 'undefined') localStorage.setItem(WEIGHT_KEY, weight);
  applyAppearance(getFont(), getScale());
  return weight;
};

/** Nudge size by ±SCALE_STEP (for the +/- buttons). Returns the new clamped value. */
export const adjustScale = (delta: number): number => setScale(getScale() + delta);

/** One-click style bundles — font + density + weight together, so a whole look
 *  is a single choice instead of three fiddly ones. */
export interface StylePreset {
  key: string; label: string; hint: string;
  font: AppFont; density: AppDensity; weight: AppWeight;
}
export const APP_PRESETS: StylePreset[] = [
  { key: 'default',     label: 'Default',      hint: 'Product Sans · compact', font: 'product-sans', density: 'compact',     weight: 'regular' },
  { key: 'geist-clean', label: 'Geist Clean',  hint: 'Geist · roomy · medium', font: 'geist',        density: 'comfortable', weight: 'medium'  },
  { key: 'comfortable', label: 'Comfortable',  hint: 'Inter · roomy',          font: 'inter',        density: 'comfortable', weight: 'regular' },
];
export const applyPreset = (p: StylePreset): void => {
  setFont(p.font); setDensity(p.density); setWeight(p.weight);
};

export const resetAppearance = (): void => {
  setFont('product-sans'); setScale(SCALE_DEFAULT); setDensity('compact'); setWeight('regular');
};

/** Call once on startup (before render) to avoid a flash of default type/size. */
export const initializeAppearance = (): void => { applyAppearance(getFont(), getScale()); };
